import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  checkAccessCase,
  extendBusinessTrial,
  fetchBusiness,
  fetchBusinesses,
  openAccessCase,
  toggleBusiness,
  updateBusinessPlan,
} from './api';
import type { PlatformAccessCase, PlatformBusiness, PlatformStaffMember, SuiteFilter } from './types';
import { daysUntil, fmtDate, statusStyle, suiteFromType } from './utils';

const PLANS = ['starter', 'pro', 'wholesale', 'enterprise', 'landlord'];

const staffStatusLabel = (status?: string) => {
  if (status === 'pending_verification') return 'invited';
  if (status === 'inactive') return 'inactive';
  return 'active';
};

const StaffList: React.FC<{ members?: PlatformStaffMember[]; compact?: boolean }> = ({ members, compact }) => {
  if (!members?.length) return <span className="plat-muted">—</span>;
  const shown = compact ? members.slice(0, 3) : members;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {shown.map((s) => (
        <div key={s.email} style={{ fontSize: 12, lineHeight: 1.35 }}>
          <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>{s.role || 'Staff'}</span>
          <span className="plat-muted"> · {s.email}</span>
          {s.status && s.status !== 'active' && (
            <span className="plat-muted"> ({staffStatusLabel(s.status)})</span>
          )}
        </div>
      ))}
      {compact && members.length > 3 && (
        <span className="plat-muted" style={{ fontSize: 11 }}>+{members.length - 3} more</span>
      )}
    </div>
  );
};

const BusinessesPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<PlatformBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PlatformBusiness | null>(null);
  const [planDraft, setPlanDraft] = useState('');
  const [activeCase, setActiveCase] = useState<PlatformAccessCase | null>(null);
  const [caseReason, setCaseReason] = useState('');

  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const suiteFilter = (searchParams.get('suite') || 'all') as SuiteFilter;
  const listFilter = searchParams.get('filter') || 'all';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setRows(await fetchBusinesses(token, {
        search: search || undefined,
        status: statusFilter,
        suite: suiteFilter,
        filter: listFilter,
      }));
    } catch {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  }, [token, search, statusFilter, suiteFilter, listFilter]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (id: number) => {
    setDrawerId(id);
    setActiveCase(null);
    setCaseReason('');
    if (!token) return;
    try {
      const [b, caseCheck] = await Promise.all([
        fetchBusiness(token, id),
        checkAccessCase(token, id).catch(() => ({ active: false, case: null })),
      ]);
      setDetail(b);
      setPlanDraft(b.plan || 'pro');
      setActiveCase(caseCheck.case || null);
    } catch {
      toast.error('Could not load business');
      setDrawerId(null);
    }
  };

  const onOpenAccessCase = async () => {
    if (!token || !detail || !caseReason.trim()) return toast.error('Enter a reason');
    setBusy(true);
    try {
      const c = await openAccessCase(token, {
        business_id: detail.id,
        reason: caseReason.trim(),
        purpose: 'support',
      });
      setActiveCase(c);
      setCaseReason('');
      toast.success('Access case opened (4h)');
    } catch {
      toast.error('Failed to open case');
    } finally {
      setBusy(false);
    }
  };

  const closeDrawer = () => {
    setDrawerId(null);
    setDetail(null);
  };

  const suspend = async () => {
    if (!detail || !token) return;
    const restoring = detail.subscription_status !== 'active';
    if (!window.confirm(`${restoring ? 'Restore' : 'Suspend'} ${detail.name}?`)) return;
    setBusy(true);
    try {
      await toggleBusiness(token, detail.id);
      toast.success(restoring ? 'Account restored' : 'Account suspended');
      await load();
      setDetail(await fetchBusiness(token, detail.id));
    } catch {
      toast.error('Action failed');
    } finally {
      setBusy(false);
    }
  };

  const savePlan = async () => {
    if (!detail || !token || !planDraft) return;
    setBusy(true);
    try {
      await updateBusinessPlan(token, detail.id, planDraft);
      toast.success('Plan updated');
      await load();
      setDetail(await fetchBusiness(token, detail.id));
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setBusy(false);
    }
  };

  const extendTrial = async () => {
    if (!detail || !token) return;
    setBusy(true);
    try {
      await extendBusinessTrial(token, detail.id, 7);
      toast.success('Trial extended by 7 days');
      await load();
      setDetail(await fetchBusiness(token, detail.id));
    } catch {
      toast.error('No registration to extend or action failed');
    } finally {
      setBusy(false);
    }
  };

  const activeFilterLabel = listFilter !== 'all'
    ? listFilter.replace(/_/g, ' ')
    : statusFilter !== 'all'
      ? statusFilter
      : null;

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>Businesses</h2>
          <p>Owner contact, plan, status, trial — plus invited staff. No customer/tenant data.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={load}>Refresh</button>
      </div>

      {activeFilterLabel && (
        <div className="plat-panel" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="plat-muted">Filter: <strong style={{ color: '#e2e8f0' }}>{activeFilterLabel}</strong></span>
          <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => setSearchParams({}, { replace: true })}>Clear</button>
        </div>
      )}

      <div className="plat-toolbar">
        <input
          className="plat-search"
          placeholder="Search name, owner or staff email…"
          value={search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <div className="plat-pills">
          {(['all', 'landlord', 'wholesale'] as SuiteFilter[]).map((s) => (
            <button key={s} type="button" className={`plat-pill${suiteFilter === s ? ' active' : ''}`} onClick={() => setFilter('suite', s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="plat-pills" style={{ marginBottom: 14 }}>
        {['all', 'active', 'trial', 'past_due', 'canceled'].map((st) => (
          <button key={st} type="button" className={`plat-pill${statusFilter === st ? ' active' : ''}`} onClick={() => setFilter('status', st)}>{st}</button>
        ))}
      </div>

      <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="plat-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="plat-empty">No businesses match your filters.</div>
        ) : (
          <div className="plat-table-wrap">
            <table className="plat-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Owner (Admin)</th>
                  <th>Staff / team</th>
                  <th>Suite</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Trial ends</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const days = daysUntil(b.trial_ends_at);
                  return (
                    <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openDrawer(b.id)}>
                      <td><div style={{ fontWeight: 700 }}>{b.name}</div></td>
                      <td>
                        <div>{b.owner_name || '—'}</div>
                        <div className="plat-muted">{b.owner_email || '—'}</div>
                      </td>
                      <td><StaffList members={b.staff_members} compact /></td>
                      <td className="plat-muted" style={{ textTransform: 'capitalize' }}>{suiteFromType(b.business_type)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{b.plan || '—'}</td>
                      <td><span style={statusStyle(b.subscription_status || 'active')}>{b.subscription_status || 'active'}</span></td>
                      <td className="plat-muted">{fmtDate(b.created_at)}</td>
                      <td className="plat-muted">
                        {b.trial_ends_at ? (
                          <>{fmtDate(b.trial_ends_at)}{days !== null && days >= 0 ? ` (${days}d)` : ''}</>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawerId != null && detail && (
        <>
          <div className="plat-drawer-backdrop" onClick={closeDrawer} aria-hidden />
          <aside className="plat-drawer" role="dialog" aria-label="Business detail">
            <button type="button" className="plat-drawer-close" onClick={closeDrawer} aria-label="Close">×</button>
            <h3>{detail.name}</h3>
            <p className="plat-muted" style={{ margin: '0 0 20px' }}>ID {detail.id} · {suiteFromType(detail.business_type)} suite</p>

            <div className="plat-detail-row"><span className="label">Owner (Admin)</span><span>{detail.owner_name || '—'}</span></div>
            <div className="plat-detail-row"><span className="label">Owner email</span><span>{detail.owner_email || '—'}</span></div>

            <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Staff &amp; roles
            </div>
            <StaffList members={detail.staff_members} />

            <div className="plat-detail-row"><span className="label">Status</span><span style={statusStyle(detail.subscription_status || 'active')}>{detail.subscription_status || 'active'}</span></div>
            <div className="plat-detail-row"><span className="label">Registration</span><span style={{ textTransform: 'capitalize' }}>{detail.registration_status || '—'}</span></div>
            <div className="plat-detail-row"><span className="label">Created</span><span>{fmtDate(detail.created_at)}</span></div>
            <div className="plat-detail-row"><span className="label">Trial ends</span><span>{fmtDate(detail.trial_ends_at)}</span></div>
            <div className="plat-detail-row"><span className="label">Period end</span><span>{fmtDate(detail.current_period_end)}</span></div>
            {detail.open_billing_tickets > 0 && (
              <div className="plat-detail-row">
                <span className="label">Billing tickets</span>
                <Link to={`/admin/billing?business_id=${detail.id}`} style={{ color: '#a5b4fc' }}>{detail.open_billing_tickets} open →</Link>
              </div>
            )}

            <div style={{ marginTop: 18, padding: 14, background: '#0f172a', borderRadius: 10, border: '1px solid #334155' }}>
              <div className="label" style={{ marginBottom: 8 }}>Sensitive access case</div>
              {activeCase ? (
                <p className="plat-muted" style={{ margin: '0 0 8px', color: '#34d399' }}>
                  Active until {fmtDate(activeCase.expires_at)} — tenant DSAR exports allowed
                </p>
              ) : (
                <>
                  <input className="plat-search" style={{ maxWidth: '100%', marginBottom: 8 }} placeholder="Reason for accessing tenant data…" value={caseReason} onChange={(e) => setCaseReason(e.target.value)} />
                  <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={busy} onClick={onOpenAccessCase}>Open case</button>
                </>
              )}
            </div>

            <div style={{ marginTop: 22 }}>
              <div className="label" style={{ marginBottom: 8 }}>Change plan</div>
              <select className="plat-select" value={planDraft} onChange={(e) => setPlanDraft(e.target.value)}>
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={savePlan}>Save plan</button>
            </div>

            <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {detail.registration_id && (
                <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" disabled={busy} onClick={extendTrial}>+7d trial</button>
              )}
              <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={busy} onClick={suspend}>
                {detail.subscription_status === 'active' ? 'Suspend' : 'Restore access'}
              </button>
              <Link to={`/admin/billing?business_id=${detail.id}`} className="plat-btn plat-btn-ghost plat-btn-sm" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                Billing & support
              </Link>
              <Link to="/admin/gdpr?tab=exports" className="plat-btn plat-btn-ghost plat-btn-sm" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                DSAR export
              </Link>
            </div>

            <p className="plat-muted" style={{ marginTop: 20, fontSize: 11, lineHeight: 1.5 }}>
              Customer and tenant portal users are not listed here. Open a billing support case or DSAR export for deeper access.
            </p>
          </aside>
        </>
      )}
    </>
  );
};

export default BusinessesPage;
